/**
 * Product Variant Components
 * Depends on: global-core.js (for requestIdleCallback polyfill)
 */

class VariantSelects extends HTMLElement {
  constructor() {
    super();
    this.domCache = {};
    this.rafId = null;
    this.debounceTimer = null;
    this.addEventListener('change', this.onVariantChange.bind(this));
  }

  onVariantChange(event) {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    this.debounceTimer = setTimeout(() => {
      this.handleVariantChange();
    }, 50);
  }

  handleVariantChange() {
    this.updateOptions();
    this.updateMasterId();

    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
    }

    this.rafId = requestAnimationFrame(() => {
      this.toggleAddButton(true, '', false);
      this.removeErrorMessage();

      if (!this.currentVariant) {
        this.toggleAddButton(true, '', true);
        this.setUnavailable();
      } else {
        this.batchDOMUpdates();
      }
    });
  }

  batchDOMUpdates() {
    this.updateMedia();
    this.updateURL();
    this.updateVariantInput();
    this.updateShareUrl();

    requestIdleCallback(
      () => {
        this.updateVariantStatuses();
        this.updatePickupAvailability();
      },
      { timeout: 100 }
    );

    this.renderProductInfo();
  }

  updateOptions() {
    this.options = Array.from(this.querySelectorAll('select'), (select) => select.value);
  }

  updateMasterId() {
    this.currentVariant = this.getVariantData().find((variant) => {
      return !variant.options
        .map((option, index) => {
          return this.options[index] === option;
        })
        .includes(false);
    });
  }

  updateMedia() {
    if (!this.currentVariant?.featured_media) return;

    if (!this.domCache.mediaGalleries) {
      this.domCache.mediaGalleries = document.querySelectorAll(`[id^="MediaGallery-${this.dataset.section}"]`);
    }

    this.domCache.mediaGalleries.forEach((mediaGallery) =>
      mediaGallery.setActiveMedia(`${this.dataset.section}-${this.currentVariant.featured_media.id}`, true)
    );

    if (!this.domCache.modalContent) {
      this.domCache.modalContent = document.querySelector(
        `#ProductModal-${this.dataset.section} .product-media-modal__content`
      );
    }

    if (!this.domCache.modalContent) return;

    const newMediaModal = this.domCache.modalContent.querySelector(
      `[data-media-id="${this.currentVariant.featured_media.id}"]`
    );
    if (newMediaModal) {
      this.domCache.modalContent.prepend(newMediaModal);
    }
  }

  updateURL() {
    if (!this.currentVariant || this.dataset.updateUrl === 'false') return;
    window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updateShareUrl() {
    if (!this.domCache.shareButton) {
      this.domCache.shareButton = document.getElementById(`Share-${this.dataset.section}`);
    }

    if (!this.domCache.shareButton?.updateUrl) return;
    this.domCache.shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updateVariantInput() {
    if (!this.domCache.productForms) {
      this.domCache.productForms = document.querySelectorAll(
        `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
      );
    }

    this.domCache.productForms.forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]');
      if (!input) return;

      input.value = this.currentVariant.id;
      this.autoSubmitQuickAddForm(input);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });

    if (!this.domCache.variantSelected) {
      this.domCache.variantSelected = document.querySelector('#ProductInfo-quick-add .js-variant-selected');
      this.domCache.productFormButtons = document.querySelector('.product-form__buttons');
    }

    if (this.domCache.variantSelected) {
      this.domCache.variantSelected.textContent = this.currentVariant.title;
      this.domCache.productFormButtons?.classList.add('selected');
    }
  }

  autoSubmitQuickAddForm(input) {
    const productInfo = this.closest('#ProductInfo-quick-add');
    const form = input.closest('form');
    if (!productInfo || !form) return;
    input.addEventListener('change', () => {
      form.dispatchEvent(
        new Event('submit', {
          bubbles: true,
          cancelable: true,
        })
      );
    }, { once: true });
  }

  updateVariantStatuses() {
    const checkedInput = this.querySelector(':checked');
    if (!checkedInput) return;

    const selectedValue = checkedInput.value;

    if (!this.domCache.inputWrappers) {
      this.domCache.inputWrappers = [...this.querySelectorAll('.product-form__input')];
    }

    const selectedOptionOneVariants = this.variantData.filter((variant) => selectedValue === variant.option1);

    this.domCache.inputWrappers.forEach((option, index) => {
      if (index === 0) return;

      const optionInputs = [...option.querySelectorAll('input[type="radio"], option')];
      const previousOptionSelected = this.domCache.inputWrappers[index - 1].querySelector(':checked')?.value;

      if (!previousOptionSelected) return;

      const availableValues = [];
      for (let i = 0; i < selectedOptionOneVariants.length; i++) {
        const variant = selectedOptionOneVariants[i];
        if (variant.available && variant[`option${index}`] === previousOptionSelected) {
          availableValues.push(variant[`option${index + 1}`]);
        }
      }

      this.setInputAvailability(optionInputs, availableValues);
    });
  }

  setInputAvailability(listOfOptions, listOfAvailableOptions) {
    listOfOptions.forEach((input) => {
      if (listOfAvailableOptions.includes(input.getAttribute('value'))) {
        input.innerText = input.getAttribute('value');
      } else {
        input.innerText = window.variantStrings.unavailable_with_option.replace('[value]', input.getAttribute('value'));
      }
    });
  }

  updatePickupAvailability() {
    if (!this.domCache.pickUpAvailability) {
      this.domCache.pickUpAvailability = document.querySelector('pickup-availability');
    }

    if (!this.domCache.pickUpAvailability) return;

    if (this.currentVariant?.available) {
      this.domCache.pickUpAvailability.fetchAvailability(this.currentVariant.id);
    } else {
      this.domCache.pickUpAvailability.removeAttribute('available');
      this.domCache.pickUpAvailability.innerHTML = '';
    }
  }

  removeErrorMessage() {
    const section = this.closest('section');
    if (!section) return;

    const productForm = section.querySelector('product-form');
    if (productForm && typeof productForm.handleErrorMessage === 'function') {
      productForm.handleErrorMessage();
    }
  }

  renderProductInfo() {
    const requestedVariantId = this.currentVariant.id;
    const sectionId = this.dataset.originalSection || this.dataset.section;

    fetch(`${this.dataset.url}?variant=${requestedVariantId}&section_id=${sectionId}`)
      .then((response) => response.text())
      .then((responseText) => {
        if (this.currentVariant.id !== requestedVariantId) return;

        requestAnimationFrame(() => {
          this.updateProductInfoDOM(responseText, sectionId);
        });
      });
  }

  updateProductInfoDOM(responseText, sectionId) {
    const html = new DOMParser().parseFromString(responseText, 'text/html');

    if (!this.domCache.priceDestination) {
      this.domCache.priceDestination = document.getElementById(`price-${this.dataset.section}`);
      this.domCache.skuDestination = document.getElementById(`Sku-${this.dataset.section}`);
      this.domCache.inventoryDestination = document.getElementById(`Inventory-${this.dataset.section}`);
    }

    const sourceSection = this.dataset.originalSection || this.dataset.section;
    const priceSource = html.getElementById(`price-${sourceSection}`);
    const skuSource = html.getElementById(`Sku-${sourceSection}`);
    const inventorySource = html.getElementById(`Inventory-${sourceSection}`);

    const updates = [];

    if (priceSource && this.domCache.priceDestination) {
      updates.push(() => {
        this.domCache.priceDestination.innerHTML = priceSource.innerHTML;
        this.domCache.priceDestination.classList.remove('visibility-hidden');
      });
    }

    if (inventorySource && this.domCache.inventoryDestination) {
      updates.push(() => {
        this.domCache.inventoryDestination.innerHTML = inventorySource.innerHTML;
        this.domCache.inventoryDestination.classList.toggle('visibility-hidden', inventorySource.innerText === '');
      });
    }

    if (skuSource && this.domCache.skuDestination) {
      updates.push(() => {
        this.domCache.skuDestination.innerHTML = skuSource.innerHTML;
        this.domCache.skuDestination.classList.toggle(
          'visibility-hidden',
          skuSource.classList.contains('visibility-hidden')
        );
      });
    }

    updates.forEach((update) => update());

    const addButtonUpdated = html.getElementById(`ProductSubmitButton-${sectionId}`);
    const addButtonText = addButtonUpdated?.querySelector(':scope > span')?.textContent?.trim();
    this.toggleAddButton(
      addButtonUpdated ? addButtonUpdated.hasAttribute('disabled') : true,
      addButtonText || window.variantStrings.soldOut,
      addButtonText
    );

    if (typeof ensureNotifyMeButtonsEnabled === 'function') {
      ensureNotifyMeButtonsEnabled();
    }

    if (typeof publish === 'function') {
      publish(PUB_SUB_EVENTS.variantChange, {
        data: {
          sectionId,
          html,
          variant: this.currentVariant,
        },
      });
    }
  }

  toggleAddButton(disable = true, text, buttonTextWhenEnabled) {
    if (!this.domCache.productForm) {
      this.domCache.productForm = document.getElementById(`product-form-${this.dataset.section}`);
    }

    if (!this.domCache.productForm) return;

    if (!this.domCache.addButton) {
      this.domCache.addButton = this.domCache.productForm.querySelector('[name="add"]');
      this.domCache.addButtonText = this.domCache.productForm.querySelector('[name="add"] > span');
    }

    if (!this.domCache.addButton) return;

    const isNotifyMeButton = this.domCache.addButton.classList.contains('product-form__submit--notify-me');
    if (isNotifyMeButton) {
      disable = false;
      buttonTextWhenEnabled = buttonTextWhenEnabled || text || window.variantStrings.soldOut;
    }

    if (disable) {
      this.domCache.addButton.setAttribute('disabled', 'disabled');
      if (text && this.domCache.addButtonText) {
        this.domCache.addButtonText.textContent = text;
      }
    } else {
      this.domCache.addButton.removeAttribute('disabled');
      this.domCache.addButton.removeAttribute('aria-disabled');
      if (this.domCache.addButtonText) {
        this.domCache.addButtonText.textContent = buttonTextWhenEnabled || text || window.variantStrings.addToCart;
      }
    }
  }

  setUnavailable() {
    if (!this.domCache.productForm) {
      this.domCache.productForm = document.getElementById(`product-form-${this.dataset.section}`);
    }

    if (!this.domCache.addButton) {
      this.domCache.addButton = this.domCache.productForm?.querySelector('[name="add"]');
      this.domCache.addButtonText = this.domCache.productForm?.querySelector('[name="add"] > span');
    }

    if (!this.domCache.priceDestination) {
      this.domCache.priceDestination = document.getElementById(`price-${this.dataset.section}`);
      this.domCache.inventoryDestination = document.getElementById(`Inventory-${this.dataset.section}`);
      this.domCache.skuDestination = document.getElementById(`Sku-${this.dataset.section}`);
    }

    if (!this.domCache.addButton) return;

    if (this.domCache.addButtonText) {
      this.domCache.addButtonText.textContent = window.variantStrings.unavailable;
    }

    if (this.domCache.priceDestination) this.domCache.priceDestination.classList.add('visibility-hidden');
    if (this.domCache.inventoryDestination) this.domCache.inventoryDestination.classList.add('visibility-hidden');
    if (this.domCache.skuDestination) this.domCache.skuDestination.classList.add('visibility-hidden');
  }

  getVariantData() {
    this.variantData = this.variantData || JSON.parse(this.querySelector('[type="application/json"]').textContent);
    return this.variantData;
  }
}

customElements.define('variant-selects', VariantSelects);

class VariantRadios extends VariantSelects {
  constructor() {
    super();

    if (this.querySelectorAll('.cs-open-modal-outstock').length > 0) {
      this.querySelectorAll('.cs-open-modal-outstock').forEach((elm) => {
        elm.addEventListener('click', () => {
          if (this.closest('.product-card-wrapper').querySelector('.restock-rocket-button')) {
            this.closest('.product-card-wrapper').querySelector('.restock-rocket-button').click();
          }
        });
      });
    }
  }

  setInputAvailability(listOfOptions, listOfAvailableOptions) {
    listOfOptions.forEach((input) => {
      if (listOfAvailableOptions.includes(input.getAttribute('value'))) {
        input.classList.remove('disabled');
      } else {
        input.classList.add('disabled');
      }
    });
  }

  updateOptions() {
    const fieldsets = Array.from(this.querySelectorAll('fieldset'));
    this.options = fieldsets.map((fieldset) => {
      return Array.from(fieldset.querySelectorAll('input')).find((radio) => radio.checked).value;
    });
  }
}

customElements.define('variant-radios', VariantRadios);

class RocketBtnSizeGuide extends HTMLElement {
  constructor() {
    super();

    this.kiwiBtn = null;
    this.isLoading = false;

    this.addEventListener('click', this.handleClick.bind(this), { passive: true });
  }

  connectedCallback() {
    const observer = new MutationObserver(() => {
      const modal = document.querySelector('.kiwi-sizing-modal');

      if (modal) {
        this.style.display = '';
        observer.disconnect();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    if (document.querySelector('.kiwi-sizing-modal')) {
      this.style.display = '';
      observer.disconnect();
    }
  }

  handleClick(e) {
    if (this.isLoading) return;
    this.isLoading = true;

    this.classList.add('loading');

    if (!this.kiwiBtn) {
      this.kiwiBtn = document.querySelector('.ks-chart-float-button');
    }

    if (this.kiwiBtn) {
      requestAnimationFrame(() => {
        this.kiwiBtn.click();

        setTimeout(() => {
          this.isLoading = false;
          this.classList.remove('loading');
        }, 500);
      });
    } else {
      this.isLoading = false;
      this.classList.remove('loading');
    }
  }
}

customElements.define('rocket-btn-size-guide', RocketBtnSizeGuide);

class RocketCsVariantSelected extends HTMLElement {
  constructor() {
    super();

    const variantsScript = this.querySelector('[type="application/json"]');
    try {
      this.productData = variantsScript ? JSON.parse(variantsScript.textContent) : null;
    } catch {
      this.productData = null;
    }
  }

  connectedCallback() {
    if (this._rocketCsBound) return;
    this._rocketCsBound = true;

    this.selectedText = this.dataset.textSelected;
    this.btnOpenOptions = this.querySelector('.product-variant-select-cs--btn') || null;
    this.elmOptions = this.querySelector('.product-variant-select-cs--list') || null;
    this.options = this.querySelectorAll('.select__select li:not(.disabled):not(.title)');
    this.elmText = this.btnOpenOptions?.querySelector('span');
    this.overlay = this.querySelector('.size-overlay-custom') || null;
    this.hasSelected = false;
    this.isAddingToCart = false;

    this.options = this.querySelectorAll(
      '.select__select li:not(.disabled):not(.variant-option--notify):not(.title)'
    );

    if (this.btnOpenOptions) {
      this.btnOpenOptions.addEventListener('click', () => {
        this.elmOptions?.classList.toggle('hidden');
        this.overlay?.classList.toggle('hidden');
        this.btnOpenOptions?.classList.toggle('open');
        this.syncStickyAtc();
      });
    }

    this.handleNotifyListCapture();
    if (this._csVariantInit) return;
    this._csVariantInit = true;

    this.options = this.querySelectorAll('.select__select li:not(.disabled):not(.title)');
    this.handleSelectedOptions();
    this.handleClickOutside();
    this.handleOverlayClick();

  }

  /**
   * Capture on each size/option list so we run before bubble listeners and before the event
   * reaches the row. Opens Restock Rocket only — does not update the hidden <select> / variant.
   */
  handleNotifyListCapture() {
    this.querySelectorAll('ul.select__select').forEach((ul) => {
      ul.addEventListener(
        'click',
        (e) => {
          const li = e.target.closest('li.variant-option--notify');
          if (!li || !ul.contains(li)) return;
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          const widget = li.querySelector('.nm-restock-widget');
          const variantId = widget?.dataset?.variantId || li.dataset?.variantId;
          const productForNotify = Array.isArray(this.productData)
            ? { variants: this.productData }
            : this.productData;
          if (variantId && productForNotify && window._RestockRocket) {
            window._RestockRocket.openModal(productForNotify, parseInt(variantId, 10));
          }
          this.closeSelected();
        },
        true
      );
    });
  }

  handleClickOutside() {
    document.addEventListener('click', (e) => {
      if (!this.contains(e.target)) {
        this.closeSelected();
      }
    });
  }

  handleOverlayClick() {
    if (this.overlay) {
      this.overlay.addEventListener('click', () => {
        this.closeSelected();
      });
    }
  }

  openPicker() {
    const atcSlot = this.closest('.product-buy-buttons__atc-slot');
    atcSlot?.classList.add('product-buy-buttons__atc-slot--panel-open');

    this.elmOptions?.classList.remove('hidden');
    this.overlay?.classList.remove('hidden');
    this.btnOpenOptions?.classList.add('open');
      this.syncStickyAtc();
  }

  openPickerDelayed() {
    window.setTimeout(() => this.openPicker(), 0);
  }

    syncStickyAtc() {
        const stickyRoot = this.closest('rocket-product-sticky-button');
        if (!stickyRoot || this.dataset.rocketVariantPicker !== 'sticky') return;
        const isOpen = this.elmOptions && !this.elmOptions.classList.contains('hidden');
        stickyRoot.classList.toggle('rocket-sticky-variant-panel-open', isOpen);
    }

  resetPickerAfterSuccessfulAdd() {
    if (!this.dataset.rocketVariantPicker) return;

    this.hasSelected = false;
    this.refreshDomRefs();

    const label = this.dataset.textSelected ?? '';
    if (this.elmText) this.elmText.textContent = label;

      if (optionValue) {
        this.triggerVariantChange(optionValue);
    this.closeSelected();
        this.hasSelected = true;
      }
  }

  refreshDomRefs() {
    this.btnOpenOptions = this.querySelector('.product-variant-select-cs--btn') || null;
    this.elmOptions = this.querySelector('.product-variant-select-cs--list') || null;
    this.overlay = this.querySelector('.size-overlay-custom') || null;
    this.elmText = this.btnOpenOptions?.querySelector('span') || null;
  }

  handleSelectedOptions() {
    if (this.options.length > 0) {
      this.options.forEach((item) => {
        item.addEventListener('click', () => {
          const optionValue = item.getAttribute('value');
          this.triggerVariantChange(optionValue);
          if (!optionValue) return;
          if (this.elmText) {
            this.elmText.textContent = optionValue;
          }
          this.closeSelected();
          this.hasSelected = true;
          if (this.dataset.rocketVariantPicker === 'sticky') {
            const variantId = this.getVariantIdFromOption(optionValue);
            if (variantId) {
              this.addToCartByVariantId(variantId);
            }
            return;
          }

          this.triggerVariantChange(optionValue);
          if (this.dataset.autoSubmit === 'true') {
            this.submitMainProductForm();
          }
        });
      });
    }
  }
  getVariantIdFromOption(optionValue) {
    if (!this.productData || !optionValue) return null;
    const variants = Array.isArray(this.productData)
      ? this.productData
      : Array.isArray(this.productData.variants)
        ? this.productData.variants
        : [];
    return variants.find((variant) => variant.option1 === optionValue)?.id || null;
  }

  async addToCartByVariantId(variantId) {
    if (!variantId || this.isAddingToCart) return;
    this.isAddingToCart = true;

    const stickyRoot = this.closest('rocket-product-sticky-button');
    const loadingSpinner = stickyRoot?.querySelector('.loading-overlay__spinner');
    loadingSpinner?.classList.remove('hidden');
    stickyRoot?.classList.add('loading');

    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', 1);

    if (cart) {
      formData.append(
        'sections',
        cart.getSectionsToRender().map((section) => section.id)
      );
      formData.append('sections_url', window.location.pathname);
      cart.setActiveElement(document.activeElement);
    }

    config.body = formData;

    try {
      const response = await fetch(`${routes.cart_add_url}`, config);
      const responseJson = await response.json();

      if (responseJson.status) {
        return;
      }

      if (!cart) {
        window.location = window.routes.cart_url;
        return;
      }

      const useCollectionCartPopup = this.shouldUsePdpCollectionCartPopup(stickyRoot, cart);
      cart.renderContents(responseJson, { open: !useCollectionCartPopup });
      if (useCollectionCartPopup) this.showPdpCollectionCartPopup(responseJson, stickyRoot);
    } catch (error) {
      console.error(error);
    } finally {
      this.isAddingToCart = false;
      loadingSpinner?.classList.add('hidden');
      stickyRoot?.classList.remove('loading');
      if (cart?.classList?.contains('is-empty')) {
        cart.classList.remove('is-empty');
      }
    }
  }

  submitMainProductForm() {
    const form = document.querySelector('product-info product-form form[data-type="add-to-cart-form"]');
    if (!form) return;

    window.setTimeout(() => {
      const variantInput = form.querySelector('[name="id"]');
      if (!variantInput?.value) return;

      form.dispatchEvent(
        new Event('submit', {
          bubbles: true,
          cancelable: true,
        })
      );
    }, 75);
  }

  getVariantIdFromOption(optionValue) {
    if (!this.productData || !optionValue) return null;
    return this.productData.find((variant) => variant.option1 === optionValue)?.id || null;
  }

  async addToCartByVariantId(variantId) {
    if (!variantId || this.isAddingToCart) return;
    this.isAddingToCart = true;

    const stickyRoot = this.closest('rocket-product-sticky-button');
    const loadingSpinner = stickyRoot?.querySelector('.loading-overlay__spinner');
    loadingSpinner?.classList.remove('hidden');
    stickyRoot?.classList.add('loading');

    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', 1);

    if (cart) {
      formData.append(
        'sections',
        cart.getSectionsToRender().map((section) => section.id)
      );
      formData.append('sections_url', window.location.pathname);
      cart.setActiveElement(document.activeElement);
    }

    config.body = formData;

    try {
      const response = await fetch(`${routes.cart_add_url}`, config);
      const responseJson = await response.json();

      if (responseJson.status) {
        return;
      }

      if (!cart) {
        window.location = window.routes.cart_url;
        return;
      }

      const useCollectionCartPopup = this.shouldUsePdpCollectionCartPopup(stickyRoot, cart);
      cart.renderContents(responseJson, { open: !useCollectionCartPopup });
      if (useCollectionCartPopup) this.showPdpCollectionCartPopup(responseJson, stickyRoot);
    } catch (error) {
      console.error(error);
    } finally {
      this.isAddingToCart = false;
      loadingSpinner?.classList.add('hidden');
      stickyRoot?.classList.remove('loading');
    }
  }

  submitMainProductForm() {
    const form = document.querySelector('product-info product-form form[data-type="add-to-cart-form"]');
    if (!form) return;

    window.setTimeout(() => {
      const variantInput = form.querySelector('[name="id"]');
      if (!variantInput?.value) return;

      form.dispatchEvent(
        new Event('submit', {
          bubbles: true,
          cancelable: true,
        })
      );
    }, 75);
  }

  shouldUsePdpCollectionCartPopup(source, cart) {
    const popup = document.querySelector('collection-cart-popup[data-collection-cart-popup="true"]');
    if (!popup) return false;
    if (!window.matchMedia('(max-width: 749px)').matches) return false;
    if (source?.dataset.collectionCartPopup !== 'true') return false;
    if (cart?.tagName?.toLowerCase() !== 'cart-drawer') return false;

    return true;
  }

  showPdpCollectionCartPopup(cartItem, source) {
    const popup = document.querySelector('collection-cart-popup[data-collection-cart-popup="true"]');
    if (!popup || typeof popup.show !== 'function') return;

    document.body.classList.remove('open-quick-add', 'open-model-drawer');
    popup.show(cartItem, source);
  }

  closeSelected() {
    this.elmOptions?.classList.add('hidden');
    this.btnOpenOptions?.classList.remove('open');
    this.overlay?.classList.add('hidden');

    this.closest('.product-buy-buttons__atc-slot')?.classList.remove('product-buy-buttons__atc-slot--panel-open');
      this.syncStickyAtc();
  }

  triggerVariantChange(optionValue) {
    if (!this.variantSelectEl) {
      const productRoot = this.closest?.('product-info') || document;
      this.variantSelectEl = productRoot.querySelector('variant-selects') || document.querySelector('variant-selects');
    }

    if (!this.variantSelectEl) return;

    if (!this.selectElements) {
      this.selectElements = this.variantSelectEl.querySelectorAll('select');
    }

    this.selectElements.forEach((select) => {
      const matchedOption = Array.from(select.options).find((opt) => opt.value === optionValue);
      if (matchedOption) {
        select.value = optionValue;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });

    requestIdleCallback(
      () => {
        if (!this.variantIdInput) {
          const productRoot = document.querySelector('product-info');
          const form = productRoot?.querySelector('product-form form');
          this.variantIdInput =
            form?.querySelector('[name="id"]') ?? document.querySelector('product-info [name="id"]');
        }

        if (this.variantIdInput?.value) {
          this.classList.remove('variant-missing');
        }
      },
      { timeout: 100 }
    );
  }
}

customElements.define('rocket-cs-variant-selected', RocketCsVariantSelected);

class RocketProductCsTabs extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.tabTitles = this.querySelectorAll('.product-cs-tabs--titles span');
    this.tabContents = this.querySelectorAll('.product-cs-tabs--content');

    this.tabTitles.forEach((title, index) => {
      title.addEventListener('click', () => {
        this.setActiveTab(index);
      });
    });

    // let activeIndex = Array.from(this.tabTitles).findIndex((title) => title.classList.contains('active'));
    // if (activeIndex === -1) activeIndex = 0;

    // this.setActiveTab(activeIndex);
  }

  setActiveTab(index) {
    this.tabTitles.forEach((title, i) => {
      title.classList.toggle('active', i === index);
    });

    this.tabContents.forEach((content, i) => {
      content.style.display = i === index ? 'block' : 'none';
    });
  }
}

customElements.define('rocket-product-cs-tabs', RocketProductCsTabs);

class RocketProductCompleteTheLook extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.initSlider();
  }

  initSlider() {
    if (this.querySelector('.swiper')) {
      new Swiper(this.querySelector('.swiper'), {
        loop: false,
        spaceBetween: 2,
        slidesPerView: 'auto',
        mousewheel: {
          forceToAxis: true,
        },
        breakpoints: {
          768: {
            spaceBetween: 13,
          },
        },
      });
    }
  }
}

customElements.define('rocket-product-complete-the-look', RocketProductCompleteTheLook);

class RocketProductStickyButton extends HTMLElement {
  constructor() {
    super();
    this.domCache = {};
    // this.initButton();
    this.initRestockWidgets();
    this.initVisibilityObserver();
  }

  initButton() {
    const stickyBtn = this.querySelector('.sticky_buy_buttons__custom-btn');
    if (!stickyBtn) return;

    stickyBtn.addEventListener('click', this.handleButtonClick.bind(this), { passive: true });
  }

  handleButtonClick(event) {

    if (!this.domCache.rocketVariantSelected) {
      this.domCache.rocketVariantSelected = this.querySelector('rocket-cs-variant-selected');
    }

      this.domCache.rocketVariantSelected?.classList.remove('variant-missing');
      this.domCache.rocketVariantSelected?.openPickerDelayed?.();
  }
    initVisibilityObserver() {
        if (!('IntersectionObserver' in window)) return;

        const root = this.closest('section') || document;
        const variantPicker = root.querySelector('.product-cs-variant-picker');
        const buyButtons = root.querySelector('.product-buy-buttons');

        const footerSection = document.querySelector('.footer');
        if (!variantPicker && !buyButtons && !footerSection) return;
        this.domCache.visibilityState = {
          variantPicker: false,
          buyButtons: false,
          footerSection: false,
        };

        // if (!variantPicker || !buyButtons) return;

        this.domCache.visibilityObserver = new IntersectionObserver(
        (entries) => {
            entries.forEach((entry) => {
              if (entry.target === variantPicker) {
                this.domCache.visibilityState.variantPicker = entry.isIntersecting;
              }
              if (entry.target === buyButtons) {
                this.domCache.visibilityState.buyButtons = entry.isIntersecting;
              }
              if (entry.target === footerSection) {
                this.domCache.visibilityState.footerSection = entry.isIntersecting;
              }
            });

            // const shouldHide =
            // this.domCache.visibilityState.variantPicker && this.domCache.visibilityState.buyButtons;
            const isMainButtonsVisible =
              this.domCache.visibilityState.variantPicker && this.domCache.visibilityState.buyButtons;

            // let isPastFooterTop = false;
            // if (footerSection) {
            //   const footerRect = footerSection.getBoundingClientRect();
            //   isPastFooterTop = footerRect.top <= window.innerHeight;
            // }
            const isPastFooterTop = footerSection
              ? footerSection.getBoundingClientRect().top <= window.innerHeight + 50
              : false;

            const shouldHide = isPastFooterTop || isMainButtonsVisible;
            this.classList.toggle('rocket-sticky-hidden', shouldHide);

            if (shouldHide) {
            this.closeStickyVariantPicker();
            }
        },
        { 
          root: null, 
          threshold: 0,
          rootMargin: '0px 0px 50px 0px'
        }
        );

        if (variantPicker) this.domCache.visibilityObserver.observe(variantPicker);
        if (buyButtons) this.domCache.visibilityObserver.observe(buyButtons);
        if (footerSection) this.domCache.visibilityObserver.observe(footerSection);
    }

    closeStickyVariantPicker() {
    if (!this.domCache.rocketVariantSelected) {
      this.domCache.rocketVariantSelected = this.querySelector('rocket-cs-variant-selected');
    }

    this.domCache.rocketVariantSelected?.closeSelected?.();
  }
  

  initRestockWidgets() {
    const restockWidgets = this.querySelectorAll('.nm-restock-widget');
    if (restockWidgets.length === 0) return;

    restockWidgets.forEach((elm) => {
      elm.addEventListener('click', this.handleRestockClick.bind(this, elm), { passive: true });
    });
  }

  handleRestockClick(elm) {
    try {
      if (!this.productDataCache) {
        const scriptTag = this.querySelector('script[type="application/json"]');
        try {
          this.productDataCache = scriptTag?.textContent ? JSON.parse(scriptTag.textContent) : null;
        } catch (e) {
          this.productDataCache = null;
        }
      }

      const variantsList = Array.isArray(this.productDataCache)
        ? this.productDataCache
        : this.productDataCache?.variants;
      const productForModal = Array.isArray(this.productDataCache)
        ? { variants: this.productDataCache }
        : this.productDataCache;

      const variantId = elm.dataset.variantId ||
        (() => {
          const selectedValue = elm.closest('.disabled')?.getAttribute('value');
          if (!variantsList || !selectedValue) return null;
          const v = variantsList.find((variant) => variant.option1 === selectedValue);
          return v ? v.id : null;
        })();

      if (variantId && productForModal && window._RestockRocket) {
        window._RestockRocket.openModal(productForModal, parseInt(variantId, 10));
      }
    } catch (error) {}
  }
}

customElements.define('rocket-product-sticky-button', RocketProductStickyButton);

class RocketGifWrapToggle extends HTMLElement {
  constructor() {
    super();
    this.domCache = {};
    this.isProcessing = false;
  }

  connectedCallback() {
    this.checkbox = this.querySelector('[data-gift-packaging-toggle]');
    if (!this.checkbox) return;

    this.variantId = this.checkbox.dataset.variantId;
    this.checkbox.addEventListener('change', this.handleCheckboxChange.bind(this), { passive: true });
  }

  handleCheckboxChange() {
    if (this.isProcessing) return;

    if (this.checkbox.checked) {
      this.addGiftWrap();
    } else {
      this.removeGiftWrap();
    }
  }

  async addGiftWrap() {
    if (this.isProcessing) return;
    this.isProcessing = true;

    try {
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: this.variantId, quantity: 1 }),
      });

      if (document.body.classList.contains('template--cart')) {
        location.reload();
      } else {
        await this.updateCartDrawerUI();
      }
    } catch (error) {
    } finally {
      this.isProcessing = false;
    }
  }

  async removeGiftWrap() {
    if (this.isProcessing) return;
    this.isProcessing = true;

    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      const item = cart.items.find((i) => i.variant_id == this.variantId);

      if (item) {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: item.key, quantity: 0 }),
        });

        if (document.body.classList.contains('template--cart')) {
          location.reload();
        } else {
          await this.updateCartDrawerUI();
        }
      }
    } catch (error) {
    } finally {
      this.isProcessing = false;
    }
  }

  async updateCartDrawerUI() {
    try {
      const response = await fetch(`${routes.cart_url}`);
      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      requestAnimationFrame(() => {
        if (!this.domCache.currentCartDrawer) {
          this.domCache.currentCartDrawer = document.querySelector('cart-drawer');
          this.domCache.currentCartCount = document.querySelector('.cart-count-bubble');
        }

        const newCartDrawer = html.querySelector('cart-drawer');
        if (newCartDrawer && this.domCache.currentCartDrawer) {
          this.domCache.currentCartDrawer.innerHTML = newCartDrawer.innerHTML;
          this.domCache.currentCartDrawer.classList.remove('is-empty');
        }

        const newCartCount = html.querySelector('.cart-count-bubble');
        if (newCartCount && this.domCache.currentCartCount) {
          this.domCache.currentCartCount.innerHTML = newCartCount.innerHTML;
        }
      });
    } catch (error) {}
  }
}

customElements.define('rocket-gif-wrap-toggle', RocketGifWrapToggle);

function ensureNotifyMeButtonsEnabled() {
  document.querySelectorAll('.product-form__submit--notify-me').forEach((btn) => {
    btn.removeAttribute('disabled');
    btn.removeAttribute('aria-disabled');
    btn.classList.remove('disabled', 'btn--disabled');
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', ensureNotifyMeButtonsEnabled);
} else {
  ensureNotifyMeButtonsEnabled();
}
